// frontend/src/components/ManageOrganization.js
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as GST_STATES from 'gst-states';
import { 
  Building2, Upload, CheckCircle, XCircle, Edit3, 
  Mail, Phone, MapPin, CreditCard, Building, 
  Save, RefreshCw, Trash2, Globe
} from 'lucide-react';

// Normalize gst-states export shape and keep only valid code->name pairs.
const GST_STATES_MAP = GST_STATES?.default && typeof GST_STATES.default === 'object'
  ? GST_STATES.default
  : GST_STATES;

const stateOptions = Object.entries(GST_STATES_MAP)
  .filter(([code, name]) => /^\d{2}$/.test(String(code)) && typeof name === 'string')
  .map(([code, name]) => ({
    code,
    name,
    label: `${name} (${code})`
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const ManageOrganization = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId, tenantId } = location.state || {};
  
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [billingDetails, setBillingDetails] = useState({
    billingName: '',
    billingAddress: '',
    city: '',
    state: '',
    stateCode: '',
    country: 'India',
    pincode: '',
    gstin: '',
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoError, setLogoError] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  
  const STANDARD_SIZE = 300;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  const fetchOrganizationDetails = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      console.log('Fetching organization for tenantId:', tenantId);
      const response = await api.get(`/api/organization/${tenantId}/manage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log('Organization response:', response.data);
      const orgData = response.data.organization;
      setOrganizationDetails(orgData);
      
      // Set billing details if available
      if (orgData.billingDetails) {
        setBillingDetails(orgData.billingDetails);
      }
      
      // Load logo
      if (orgData.logoFileId) {
        setLogoLoading(true);
        try {
          const logoUrl = `${BACKEND_URL}/api/files/${encodeURIComponent(orgData.logoFileId)}?t=${Date.now()}`;
          setLogoPreview(logoUrl);
        } catch (err) {
          console.error('Error loading logo:', err);
          setLogoPreview('');
        } finally {
          setLogoLoading(false);
        }
      }
    } catch (err) {
      console.error('Error fetching organization:', err);
      setSubmitStatus({
        success: false,
        message: err.response?.data?.message || 'Error fetching organization details.'
      });
    }
  }, [tenantId, BACKEND_URL]);

  useEffect(() => {
    if (tenantId) {
      fetchOrganizationDetails();
    } else {
      navigate('/tenant-admin/admin', { state: { userId, tenantId } });
    }
  }, [tenantId, navigate, userId, fetchOrganizationDetails]);

  const processImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = STANDARD_SIZE;
          canvas.height = STANDARD_SIZE;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, STANDARD_SIZE, STANDARD_SIZE);
          canvas.toBlob(
            (blob) => {
              resolve(blob);
            },
            'image/jpeg',
            0.8
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    setLogoError('');
    if (!file) return;
    
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setLogoError('Logo must be a JPEG or PNG image.');
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      setLogoError('Logo must be less than 1MB.');
      return;
    }
    
    setLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    
    // Clear errors for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-populate state code when state is selected
    if (name === 'state') {
      const selectedState = stateOptions.find(s => s.name === value);
      setBillingDetails(prev => ({
        ...prev,
        state: value,
        stateCode: selectedState ? selectedState.code : ''
      }));
      return;
    }

    // Auto-populate state name when state code is entered
    if (name === 'stateCode') {
      const selectedState = stateOptions.find(s => s.code === value);
      setBillingDetails(prev => ({
        ...prev,
        stateCode: value,
        state: selectedState ? selectedState.name : ''
      }));
      return;
    }

    setBillingDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrganizationDetails(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (value) => {
    const formattedValue = value.startsWith('+') ? value : `+${value}`;
    setOrganizationDetails(prev => ({ ...prev, phone: formattedValue }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Organization validation
    if (!organizationDetails?.organizationName?.trim()) {
      newErrors.organizationName = 'Organization name is required';
      isValid = false;
    }

    if (!organizationDetails?.address?.trim()) {
      newErrors.address = 'Address is required';
      isValid = false;
    }

    if (!organizationDetails?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
      isValid = false;
    } else {
      const fullNumber = organizationDetails.phone.startsWith('+') 
        ? organizationDetails.phone 
        : `+${organizationDetails.phone}`;
      const phoneNumber = parsePhoneNumberFromString(fullNumber);
      if (!phoneNumber || !phoneNumber.isValid()) {
        newErrors.phone = 'Invalid global phone number format';
        isValid = false;
      }
    }

    if (!organizationDetails?.email?.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(organizationDetails.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    // Billing validation - simple checks
    const hasBillingData = Object.values(billingDetails).some(val => val && val.trim().length > 0);
    
    if (hasBillingData) {
      if (billingDetails.gstin && billingDetails.gstin.trim()) {
        const gstin = billingDetails.gstin.trim().replace(/\s/g, '');
        if (gstin.length !== 15) {
          newErrors.gstin = 'GSTIN must be 15 characters';
          isValid = false;
        }
      }

      if (billingDetails.pincode && billingDetails.pincode.trim()) {
        if (!/^[0-9]{6}$/.test(billingDetails.pincode.trim())) {
          newErrors.pincode = 'Pincode must be 6 digits';
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });

    let uploadedLogoFileId = organizationDetails.logoFileId;
    let uploadedLogoUrl = organizationDetails.logoUrl;

    try {
      const currentTenantId = tenantId || location.state?.tenantId;
      
      if (!currentTenantId) {
        throw new Error('Tenant ID is missing');
      }
      
      // Upload new logo if changed
      if (logoFile) {
        const processedBlob = await processImage(logoFile);
        const formData = new FormData();
        formData.append('file', processedBlob, logoFile.name.replace(/\.[^/.]+$/, ".jpg"));
        
        const uploadRes = await api.post(`/api/organization/${currentTenantId}/logo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedLogoFileId = uploadRes.data.fileId;
        uploadedLogoUrl = uploadRes.data.downloadUrl || uploadRes.data.fileUrl;
        console.log('Logo uploaded to R2, fileId:', uploadedLogoFileId);
      }

      // Prepare billing details
      const hasBillingData = Object.values(billingDetails).some(val => val && val.trim().length > 0);
      const billingPayload = hasBillingData ? {
        billingName: billingDetails.billingName || null,
        billingAddress: billingDetails.billingAddress || null,
        city: billingDetails.city || null,
        state: billingDetails.state || null,
        stateCode: billingDetails.stateCode || null,
        country: billingDetails.country || 'India',
        pincode: billingDetails.pincode || null,
        gstin: billingDetails.gstin ? billingDetails.gstin.trim().toUpperCase() : null,
      } : null;

      const token = localStorage.getItem('token');
      await api.put(
        `/api/organization/${currentTenantId}/manage`,
        {
          organizationName: organizationDetails.organizationName,
          address: organizationDetails.address,
          phone: organizationDetails.phone,
          email: organizationDetails.email,
          logoFileId: uploadedLogoFileId,
          logoUrl: uploadedLogoUrl,
          billingDetails: billingPayload
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubmitStatus({
        success: true,
        message: 'Organization updated successfully!'
      });
      
      setIsEditing(false);
      setLogoFile(null);
      
      await fetchOrganizationDetails();
      
    } catch (err) {
      console.error('Update error:', err);
      setSubmitStatus({
        success: false,
        message: err.response?.data?.message || 'Error updating organization. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveBilling = async () => {
    if (!window.confirm('Are you sure you want to remove all billing details?')) {
      return;
    }

    setIsSubmitting(true);
    try {
      const currentTenantId = tenantId || location.state?.tenantId;
      const token = localStorage.getItem('token');
      
      await api.put(
        `/api/organization/${currentTenantId}/billing`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBillingDetails({
        billingName: '',
        billingAddress: '',
        city: '',
        state: '',
        stateCode: '',
        country: 'India',
        pincode: '',
        gstin: '',
      });
      
      setSubmitStatus({
        success: true,
        message: 'Billing details removed successfully'
      });
      
      await fetchOrganizationDetails();
      
    } catch (err) {
      console.error('Error removing billing:', err);
      setSubmitStatus({
        success: false,
        message: 'Error removing billing details'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setLogoFile(null);
    setLogoError('');
    if (organizationDetails?.logoFileId) {
      fetchOrganizationDetails();
    } else {
      setLogoPreview('');
    }
    setErrors({});
  };

  if (!organizationDetails) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 font-medium text-slate-600 text-sm">Loading workspace profiles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 md:py-12 flex flex-col items-center">
      <div className="max-w-6xl w-full">
        
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="mx-auto h-14 w-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-md shadow-blue-600/10 mb-4">
            <Building2 className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Organization Profile
          </h1>
          <p className="mt-2 text-sm md:text-base text-slate-500 max-w-md mx-auto leading-relaxed">
            Configure institutional system details, branding assets, and billing settings.
          </p>
        </div>

        {/* Status Alert */}
        {submitStatus.message && (
          <div className={`mb-6 p-4 rounded-xl flex gap-3 max-w-5xl mx-auto ${
            submitStatus.success 
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}>
            {submitStatus.success ? (
              <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
            )}
            <p className="text-xs font-semibold leading-relaxed">{submitStatus.message}</p>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            
            {/* Left Panel - Logo */}
            <div className="w-full lg:w-4/12 bg-slate-50/60 p-6 md:p-8 flex flex-col items-center justify-center border-b border-slate-200 lg:border-b-0 lg:border-r border-slate-100">
              <div className="text-center mb-6">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Institution Logo</h3>
                <p className="text-[11px] text-slate-400 mt-1">Institutional visual profile identifier</p>
              </div>
              
              <div className="w-full max-w-[180px]">
                <div className="relative aspect-square group">
                  <div className="w-full h-full rounded-2xl border border-slate-200 bg-white shadow-sm flex items-center justify-center overflow-hidden relative">
                    {logoLoading ? (
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : logoPreview ? (
                      <img 
                        src={logoPreview} 
                        alt="Organization Logo" 
                        className="w-full h-full object-contain p-3 select-none"
                      />
                    ) : (
                      <div className="text-center p-4 select-none">
                        <Upload className="w-8 h-8 text-slate-300 mx-auto mb-1" />
                        <span className="text-[10px] font-medium text-slate-400 block">No logo uploaded</span>
                      </div>
                    )}

                    {isEditing && !logoLoading && (
                      <label 
                        htmlFor="logo-upload" 
                        className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all cursor-pointer rounded-2xl"
                      >
                        <span className="text-[10px] font-bold text-white bg-slate-950/80 px-2.5 py-1.5 rounded-lg shadow-sm">
                          Change Logo
                        </span>
                        <input
                          id="logo-upload"
                          name="logo"
                          type="file"
                          accept="image/png, image/jpeg"
                          onChange={handleLogoChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>
                
                {isEditing && logoError && (
                  <p className="mt-2.5 text-[11px] text-rose-600 font-medium text-center flex items-center justify-center gap-1">
                    <XCircle className="w-3 h-3 flex-shrink-0" />
                    {logoError}
                  </p>
                )}
                
                {isEditing && !logoError && (
                  <p className="text-[10px] text-slate-400 text-center mt-3 leading-tight select-none">
                    PNG or JPG format up to 1MB
                  </p>
                )}
              </div>

              {/* Billing Status Indicator */}
              {organizationDetails.billingDetails?.stateCode && (
                <div className="mt-6 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Billing Information Added
                  </p>
                </div>
              )}
            </div>

            {/* Right Panel - Content */}
            <div className="w-full lg:w-8/12 p-5 md:p-8">
              
              {/* Header Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-slate-100 mb-6">
                <div>
                  <h2 className="text-base font-bold text-slate-900">Workspace Records</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Review and manage settings profile details.</p>
                </div>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm self-start sm:self-center"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    Edit Profile
                  </button>
                )}
              </div>

              {!isEditing ? (
                /* View Mode */
                <div className="space-y-6">
                  {/* Organization Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-slate-400 mb-1">
                        <Building2 className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Organization</span>
                      </div>
                      <p className="text-sm font-semibold text-slate-800">{organizationDetails.organizationName}</p>
                    </div>

                    <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-slate-400 mb-1">
                        <Mail className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Email</span>
                      </div>
                      <p className="text-sm font-semibold text-slate-800 truncate">{organizationDetails.email}</p>
                    </div>

                    <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-slate-400 mb-1">
                        <Phone className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Phone</span>
                      </div>
                      <p className="text-sm font-semibold text-slate-800">{organizationDetails.phone}</p>
                    </div>

                    <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-slate-400 mb-1">
                        <MapPin className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Address</span>
                      </div>
                      <p className="text-sm font-semibold text-slate-800 leading-relaxed">{organizationDetails.address}</p>
                    </div>
                  </div>

                  {/* Billing Info */}
                  {organizationDetails.billingDetails && Object.values(organizationDetails.billingDetails).some(v => v) && (
                    <div className="border-t border-slate-200 pt-4">
                      <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
                        <CreditCard className="w-4 h-4 text-blue-600" />
                        Billing Information
                      </h3>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {organizationDetails.billingDetails.billingName && (
                          <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-4">
                            <div className="flex items-center gap-2 text-slate-400 mb-1">
                              <Building className="w-3.5 h-3.5 text-blue-600" />
                              <span className="text-[10px] font-bold uppercase tracking-wider">Billing Name</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-800">
                              {organizationDetails.billingDetails.billingName}
                            </p>
                          </div>
                        )}

                        {organizationDetails.billingDetails.gstin && (
                          <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-4">
                            <div className="flex items-center gap-2 text-slate-400 mb-1">
                              <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                              <span className="text-[10px] font-bold uppercase tracking-wider">GSTIN</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-800">
                              {organizationDetails.billingDetails.gstin}
                            </p>
                          </div>
                        )}

                        {organizationDetails.billingDetails.billingAddress && (
                          <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-4">
                            <div className="flex items-center gap-2 text-slate-400 mb-1">
                              <MapPin className="w-3.5 h-3.5 text-blue-600" />
                              <span className="text-[10px] font-bold uppercase tracking-wider">Address</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-800 leading-relaxed">
                              {organizationDetails.billingDetails.billingAddress}
                            </p>
                          </div>
                        )}

                        {(organizationDetails.billingDetails.city || organizationDetails.billingDetails.state || organizationDetails.billingDetails.pincode) && (
                          <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-4">
                            <div className="flex items-center gap-2 text-slate-400 mb-1">
                              <Globe className="w-3.5 h-3.5 text-blue-600" />
                              <span className="text-[10px] font-bold uppercase tracking-wider">Location</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-800">
                              {[
                                organizationDetails.billingDetails.city,
                                organizationDetails.billingDetails.state,
                                organizationDetails.billingDetails.pincode
                              ].filter(Boolean).join(', ')}
                              {organizationDetails.billingDetails.stateCode && ` (${organizationDetails.billingDetails.stateCode})`}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Edit Mode */
                <form onSubmit={handleUpdateOrganization} className="space-y-6">
                  
                  {/* Organization Details */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
                      <Building className="w-4 h-4 text-blue-600" />
                      Organization Details
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="organizationName" className="block text-xs font-semibold text-slate-700 mb-1">
                          Organization Name <span className="text-rose-500">*</span>
                        </label>
                        <input
                          id="organizationName"
                          name="organizationName"
                          type="text"
                          value={organizationDetails.organizationName}
                          onChange={handleChange}
                          className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                            errors.organizationName ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="Enter organization name"
                        />
                        {errors.organizationName && (
                          <p className="mt-1 text-xs text-rose-600 font-medium">{errors.organizationName}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
                            Email <span className="text-rose-500">*</span>
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            value={organizationDetails.email}
                            onChange={handleChange}
                            className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                              errors.email ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                            }`}
                            placeholder="admin@institution.edu"
                          />
                          {errors.email && (
                            <p className="mt-1 text-xs text-rose-600 font-medium">{errors.email}</p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="phone" className="block text-xs font-semibold text-slate-700 mb-1">
                            Phone <span className="text-rose-500">*</span>
                          </label>
                          <PhoneInput
                            country={'us'}
                            value={organizationDetails.phone}
                            onChange={handlePhoneChange}
                            placeholder="+1 (555) 000-0000"
                            inputProps={{ id: 'phone', name: 'phone', required: true }}
                            containerClass="w-full"
                            inputClass={`!w-full !text-sm !h-[40px] !border !rounded-lg focus:!outline-none focus:!ring-2 focus:!ring-blue-500/20 !transition-all ${
                              errors.phone ? '!border-rose-300 !bg-rose-50/10 focus:!border-rose-500' : '!border-slate-200 focus:!border-blue-500'
                            }`}
                            buttonClass="!border !border-slate-200 !rounded-l-lg !bg-slate-50"
                          />
                          {errors.phone && (
                            <p className="mt-1 text-xs text-rose-600 font-medium">{errors.phone}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label htmlFor="address" className="block text-xs font-semibold text-slate-700 mb-1">
                          Address <span className="text-rose-500">*</span>
                        </label>
                        <textarea
                          id="address"
                          name="address"
                          rows={2}
                          value={organizationDetails.address}
                          onChange={handleChange}
                          className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all resize-none ${
                            errors.address ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                          }`}
                          placeholder="Enter complete address"
                        />
                        {errors.address && (
                          <p className="mt-1 text-xs text-rose-600 font-medium">{errors.address}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Billing Details */}
                  <div className="border-t border-slate-200 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-blue-600" />
                        Billing Information
                        <span className="text-xs font-normal text-slate-400">(Optional)</span>
                      </h3>
                      
                      {organizationDetails.billingDetails && Object.values(organizationDetails.billingDetails).some(v => v) && (
                        <button
                          type="button"
                          onClick={handleRemoveBilling}
                          className="text-xs text-rose-600 hover:text-rose-700 font-semibold flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="billingName" className="block text-xs font-semibold text-slate-700 mb-1">
                            Billing Name
                          </label>
                          <input
                            id="billingName"
                            name="billingName"
                            type="text"
                            value={billingDetails.billingName || ''}
                            onChange={handleBillingChange}
                            className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                              errors.billingName ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                            }`}
                            placeholder="Legal business name"
                          />
                          {errors.billingName && (
                            <p className="mt-1 text-xs text-rose-600 font-medium">{errors.billingName}</p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="gstin" className="block text-xs font-semibold text-slate-700 mb-1">
                            GSTIN
                            <span className="text-xs font-normal text-slate-400 ml-1">(Optional)</span>
                          </label>
                          <input
                            id="gstin"
                            name="gstin"
                            type="text"
                            value={billingDetails.gstin || ''}
                            onChange={handleBillingChange}
                            className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all uppercase ${
                              errors.gstin ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                            }`}
                            placeholder="27AABCG1234D1Z5"
                            maxLength="15"
                          />
                          {errors.gstin && (
                            <p className="mt-1 text-xs text-rose-600 font-medium">{errors.gstin}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label htmlFor="billingAddress" className="block text-xs font-semibold text-slate-700 mb-1">
                          Billing Address
                        </label>
                        <textarea
                          id="billingAddress"
                          name="billingAddress"
                          rows={2}
                          value={billingDetails.billingAddress || ''}
                          onChange={handleBillingChange}
                          className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                          placeholder="Registered office address"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label htmlFor="city" className="block text-xs font-semibold text-slate-700 mb-1">
                            City
                          </label>
                          <input
                            id="city"
                            name="city"
                            type="text"
                            value={billingDetails.city || ''}
                            onChange={handleBillingChange}
                            className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                            placeholder="Mumbai"
                          />
                        </div>

                        <div>
                          <label htmlFor="state" className="block text-xs font-semibold text-slate-700 mb-1">
                            State
                          </label>
                          <select
                            id="state"
                            name="state"
                            value={billingDetails.state || ''}
                            onChange={handleBillingChange}
                            className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all bg-white"
                          >
                            <option value="">Select State</option>
                            {stateOptions.map((state) => (
                              <option key={state.code} value={state.name}>
                                {state.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label htmlFor="stateCode" className="block text-xs font-semibold text-slate-700 mb-1">
                            State Code
                            <span className="text-xs font-normal text-slate-400 ml-1">(Auto)</span>
                          </label>
                          <input
                            id="stateCode"
                            name="stateCode"
                            type="text"
                            value={billingDetails.stateCode || ''}
                            onChange={handleBillingChange}
                            className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all bg-slate-50"
                            placeholder="27"
                            maxLength="2"
                          />
                          <p className="text-[10px] text-slate-400 mt-1">Auto-filled when state is selected</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="pincode" className="block text-xs font-semibold text-slate-700 mb-1">
                            Pincode
                          </label>
                          <input
                            id="pincode"
                            name="pincode"
                            type="text"
                            value={billingDetails.pincode || ''}
                            onChange={handleBillingChange}
                            className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                              errors.pincode ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                            }`}
                            placeholder="400001"
                            maxLength="6"
                          />
                          {errors.pincode && (
                            <p className="mt-1 text-xs text-rose-600 font-medium">{errors.pincode}</p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="country" className="block text-xs font-semibold text-slate-700 mb-1">
                            Country
                          </label>
                          <input
                            id="country"
                            name="country"
                            type="text"
                            value={billingDetails.country || 'India'}
                            onChange={handleBillingChange}
                            className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                            placeholder="India"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-end gap-3.5 pt-4 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="w-full sm:w-auto px-5 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-xs font-semibold transition-colors order-2 sm:order-1"
                    >
                      Cancel Changes
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto flex justify-center items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors order-1 sm:order-2 disabled:opacity-60"
                    >
                      {isSubmitting ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-3.5 h-3.5" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageOrganization;